import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  faArrowUpRightFromSquare,
  faCircleNotch,
} from '@fortawesome/free-solid-svg-icons';
import { TableLazyLoadEvent } from 'primeng/table';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UpdateSurveyContactAsync$Json$Params } from '../../../api/fn/survey-contact/update-survey-contact-async-json';
import {
  ClientSurveyContactModel,
  ClientSurveyReportModel,
  ClientSurveyTypeModel,
  EmployeeModel,
} from '../../../api/models';
import { ReportService, SurveyContactService } from '../../../api/services';
import { AccountInfoService } from '../../../services/account-info.service';
import { ToastService } from '../../../services/toast.service';
import { DateTimeUtils } from '../../../utils/datetime.utils';
import { BaseAccountInfoComponent } from '../../BaseAccountInfo/BaseAccountInfo.component';
import { GetClientSurveysReport$Json$Params } from '../../../api/fn/report/get-client-surveys-report-json';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-clients-to-call-grid',
  templateUrl: './clients-to-call-grid.component.html',
  styleUrls: ['./clients-to-call-grid.component.scss'],
})
export class ClientsToCallGridComponent
  extends BaseAccountInfoComponent
  implements OnInit, OnDestroy
{
  @Input() surveyType: ClientSurveyTypeModel;
  @Input() activeSurveyTypeId: number;
  @Input() tabIndex: number;

  month: number;
  year: number;
  reps: EmployeeModel[];
  attorneyName: string = '';
  matterName: string = '';
  matterNumber: string = '';
  searchRepId: number | undefined;
  private attorneySearchSubject = new Subject<string>();
  private matterNameSearchSubject = new Subject<string>();
  private matterNumberSearchSubject = new Subject<string>();

  areClientsLoading = true;
  isTotalRecordsLoading = false;
  faCircleNotch = faCircleNotch;
  faArrowUpRightFromSquare = faArrowUpRightFromSquare;

  first = 0;
  rows = 20;
  last = this.first + this.rows;
  totalRecords = 0;

  form: FormGroup;

  reports: ClientSurveyReportModel[] = [];

  currentClientSurveyContactModel: ClientSurveyContactModel;

  constructor(
    private router: Router,
    private reportService: ReportService,
    private surveyContactService: SurveyContactService,
    private toastService: ToastService,
    protected override accountInfoService: AccountInfoService
  ) {
    super(accountInfoService);
  }

  override ngOnInit() {
    super.ngOnInit();

    // Set up debounced search for attorney name
    this.attorneySearchSubject
      .pipe(
        debounceTime(environment.searchDebounceTime),
        distinctUntilChanged()
      )
      .subscribe((searchTerm) => {
        this.first = 0; // Reset to first page when searching

        // Update query params with all search parameters
        const queryParams: any = {
          surveyType: this.activeSurveyTypeId,
          first: 0,
          rows: this.rows,
          attorneyName: searchTerm || null,
          matterName: this.matterName || null,
          matterNumber: this.matterNumber || null,
          repId: this.searchRepId || null,
        };

        this.router.navigate([], {
          queryParams: queryParams,
          queryParamsHandling: 'merge',
        });
      });

    // Set up debounced search for matter name
    this.matterNameSearchSubject
      .pipe(
        debounceTime(environment.searchDebounceTime),
        distinctUntilChanged()
      )
      .subscribe((searchTerm) => {
        this.first = 0; // Reset to first page when searching

        // Update query params with all search parameters
        const queryParams: any = {
          surveyType: this.activeSurveyTypeId,
          first: 0,
          rows: this.rows,
          attorneyName: this.attorneyName || null,
          matterName: searchTerm || null,
          matterNumber: this.matterNumber || null,
          repId: this.searchRepId || null,
        };

        this.router.navigate([], {
          queryParams: queryParams,
          queryParamsHandling: 'merge',
        });
      });

    // Set up debounced search for matter number
    this.matterNumberSearchSubject
      .pipe(
        debounceTime(environment.searchDebounceTime),
        distinctUntilChanged()
      )
      .subscribe((searchTerm) => {
        this.first = 0; // Reset to first page when searching

        // Update query params with all search parameters
        const queryParams: any = {
          surveyType: this.activeSurveyTypeId,
          first: 0,
          rows: this.rows,
          attorneyName: this.attorneyName || null,
          matterName: this.matterName || null,
          matterNumber: searchTerm || null,
          repId: this.searchRepId || null,
        };

        this.router.navigate([], {
          queryParams: queryParams,
          queryParamsHandling: 'merge',
        });
      });
  }

  override ngOnDestroy() {
    this.attorneySearchSubject.complete();
    this.matterNameSearchSubject.complete();
    this.matterNumberSearchSubject.complete();
  }

  onAttorneySearch(searchTerm: string) {
    this.attorneySearchSubject.next(searchTerm);
  }

  onAttorneySearchEnter(currentValue: string) {
    // Trigger immediate search on Enter key press with current input value
    this.attorneyName = currentValue; // Update the model with current value
    this.first = 0; // Reset to first page when searching

    const queryParams: any = {
      surveyType: this.activeSurveyTypeId,
      first: 0,
      rows: this.rows,
      attorneyName: currentValue || null,
      matterName: this.matterName || null,
      matterNumber: this.matterNumber || null,
      repId: this.searchRepId || null,
    };

    this.router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  onMatterNameSearch(searchTerm: string) {
    this.matterNameSearchSubject.next(searchTerm);
  }

  onMatterNameSearchEnter(currentValue: string) {
    // Trigger immediate search on Enter key press with current input value
    this.matterName = currentValue; // Update the model with current value
    this.first = 0; // Reset to first page when searching

    const queryParams: any = {
      surveyType: this.activeSurveyTypeId,
      first: 0,
      rows: this.rows,
      attorneyName: this.attorneyName || null,
      matterName: currentValue || null,
      matterNumber: this.matterNumber || null,
      repId: this.searchRepId || null,
    };

    this.router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  onMatterNumberSearch(searchTerm: string) {
    this.matterNumberSearchSubject.next(searchTerm);
  }

  onMatterNumberSearchEnter(currentValue: string) {
    // Trigger immediate search on Enter key press with current input value
    this.matterNumber = currentValue; // Update the model with current value
    this.first = 0; // Reset to first page when searching

    const queryParams: any = {
      surveyType: this.activeSurveyTypeId,
      first: 0,
      rows: this.rows,
      attorneyName: this.attorneyName || null,
      matterName: this.matterName || null,
      matterNumber: currentValue || null,
      repId: this.searchRepId || null,
    };

    this.router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  onRepSearch(repId: number | undefined) {
    // Trigger immediate search when rep is selected
    this.first = 0; // Reset to first page when searching

    const queryParams: any = {
      surveyType: this.activeSurveyTypeId,
      first: 0,
      rows: this.rows,
      attorneyName: this.attorneyName || null,
      matterName: this.matterName || null,
      matterNumber: this.matterNumber || null,
      repId: repId || null,
    };

    this.router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  getClientsToCallOnLazy(event: TableLazyLoadEvent) {
    // Update pagination data
    if (this.surveyType.id === this.activeSurveyTypeId) {
      this.first = event.first || 0;
      this.rows = event.rows || this.rows;
      this.getClientsToCall(
        this.year,
        this.month,
        this.reps,
        this.first,
        this.rows,
        this.attorneyName,
        this.matterName,
        this.matterNumber,
        this.searchRepId,
        event
      );
    }
  }

  pageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
    const queryParams: any = {
      surveyType: this.activeSurveyTypeId,
      first: this.first,
      rows: this.rows,
      attorneyName: this.attorneyName || null,
      matterName: this.matterName || null,
      matterNumber: this.matterNumber || null,
      repId: this.searchRepId || null,
    };

    this.router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge', // Merge with existing query params
    });
  }

  getClientsToCall(
    year: number,
    month: number,
    reps: EmployeeModel[],
    first: number,
    rows: number,
    attorneyName: string = '',
    matterName: string = '',
    matterNumber: string = '',
    searchRepId?: number,
    event?: any
  ) {
    if (!this.areClientsLoading) {
      // Avoid concurrent duplicate http requests if already loading.
      return;
    }
    // Make sure we don't have concurrent duplicate http requests.
    if (year === undefined || month === undefined || reps === undefined) {
      return;
    }
    (this.first = first), (this.rows = rows), (this.year = year);
    this.month = month;
    this.reps = reps;
    this.attorneyName = attorneyName;
    this.matterName = matterName;
    this.matterNumber = matterNumber;
    this.searchRepId = searchRepId;
    this.areClientsLoading = true;

    console.log('isadmin', this.accountInfo?.isAdmin);
    console.log('repId', this.accountInfo?.repId);
    console.log('searchRepId', searchRepId);

    // Determine which repId to use for filtering
    let filterRepId: number | undefined;
    if (this.accountInfo?.isAdmin) {
      // Admin: use searchRepId if provided, otherwise undefined (show all)
      filterRepId = searchRepId || undefined;
    } else {
      // Non-admin: always use their own repId
      filterRepId = this.accountInfo?.repId!;
    }

    let params: GetClientSurveysReport$Json$Params = {
      repId: filterRepId,
      surveyTypeId: this.surveyType.id,
      month: this.month,
      year: this.year,
      take: this.rows,
      skip: this.first,
      attorneyName: this.attorneyName || undefined,
      matterName: this.matterName || undefined,
      matterNumber: this.matterNumber || undefined,
    };

    this.reportService.getClientSurveysReport$Json$Response(params).subscribe({
      next: (r) => {
        this.areClientsLoading = false;
        if (!r.body) {
          this.toastService.showToast(
            'danger',
            'Problem loading clients to call'
          );
        } else {
          this.totalRecords = r.body.totalReportsCount!;

          this.reports = r.body.reports!.map((m) => {
            // Check if the repId is within the list of authorized reps.
            m.clientRepId = this.reps.find(
              (r) => r.employeeId === m.clientRepId
            )
              ? m.clientRepId
              : undefined;
            return m;
          });
        }
      },
      error: (error) => {
        console.error(error, this);
        this.areClientsLoading = false;
        this.toastService.showToast(
          'danger',
          'Problem loading clients to call'
        );
      },
    });
  }

  onRepChange(m: ClientSurveyReportModel): void {
    // Get survey contacts so we know whether to create or update.
    this.getSurveyContact(m);
  }

  getSurveyContact(m: ClientSurveyReportModel) {
    this.surveyContactService
      .getSurveyContactAsync$Json$Response({
        surveyTypeId: this.surveyType.id!,
        caseId: m.matterId!,
        month: this.month,
        year: this.year,
      })
      .subscribe({
        next: (r) => {
          this.currentClientSurveyContactModel = r.body;
          if (!r.body) {
            // No survey contact, so create one.
            this.submitContactRecord(m, true);
          } else {
            // We have a survey contact, so update it.
            this.submitContactRecord(m, false);
          }
        },
        error: (error) => {
          console.error(error, this);
          this.toastService.showToast(
            'danger',
            'Problem loading survey contact'
          );
        },
      });
  }

  submitContactRecord(m: ClientSurveyReportModel, isCreate: boolean = true) {
    if (isCreate) {
      // Create
      this.surveyContactService
        .createSurveyContactAsync$Json$Response({
          body: {
            surveyTypeId: this.surveyType.id!,
            clientRepId: m.clientRepId!,
            caseId: m.matterId!,
            month: this.month,
            year: this.year,
          },
        })
        .subscribe({
          next: (r) => {
            //this.isSurveyHistoryLoading = false;
            if (!r.body) {
              console.error(r.body);
              this.toastService.showToast(
                'danger',
                'Problem adding contact record'
              );
            } else {
              // Find the rep.
              m.clientRepId = this.reps.find(
                (r) => r.employeeId === m.clientRepId
              )?.employeeId;
              this.toastService.showToast(
                'success',
                'Rep assigned successfully'
              );
            }
          },
          error: (error) => {
            console.error(error, this);
            this.toastService.showToast(
              'danger',
              'Problem adding contact history record'
            );
          },
        });
    } else {
      // Update
      let params: UpdateSurveyContactAsync$Json$Params = {
        surveyContactId:
          this.currentClientSurveyContactModel.surveyContactId!.toString(),
        body: {
          clientRepId: m.clientRepId!,
          note: this.currentClientSurveyContactModel.note,
          surveyContactId:
            this.currentClientSurveyContactModel.surveyContactId!,
          month: DateTimeUtils.getCurrentMonthNumber(),
          year: DateTimeUtils.getCurrentYear(),
        },
      };
      this.surveyContactService
        .updateSurveyContactAsync$Json$Response(params)
        .subscribe({
          next: (r) => {
            //this.isSurveyHistoryLoading = false;
            if (!r.body) {
              console.error(r.body);
              this.toastService.showToast(
                'danger',
                'Problem adding contact history record'
              );
            } else {
              this.toastService.showToast(
                'success',
                'Rep assigned successfully'
              );
            }
          },
          error: (error) => {
            console.error(error, this);
            this.toastService.showToast(
              'danger',
              'Problem adding contact history record'
            );
          },
        });
    }
  }
}
